import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    Close as CloseIcon,
    WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import UserRole from '../../../../shared/AuthRoles';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { FormProvider, useForm, useFormState, useWatch } from 'react-hook-form';
import OwnerCompanyDetails from './OwnerCompanyDetails';
import GuestCompanyDetails from './GuestCompanyDetails';
import PersonalDetailsComponent from './PersonalDetails';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import toast from '../../../shared/Toast';
import {
    useEditCompanyMutation,
    useEditProfileDetailsMutation,
    useGetProfileDetailsQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import { ArrowBack } from '@mui/icons-material';
import { Media } from '../../../types/user';
import ContractorCompanyDetails from './Contractor/ContractorCompanyDetails';
import { getProfileSchema } from './schema';
import {
    ProfileInlineEditSection,
    ProfileInlineUxContext,
    type ProfilePhotoInlineController,
} from '../ProfileInlineUxContext';
import {
    profileInlineSectionHasDirtyChanges,
    profilePictureIsDirty,
} from '../profileInlineSectionDirty';
import { profileMobileInlineEditBottomReserve } from '../ProfileSectionCard';
import ProfileSetupIncompleteCard from '../ProfileSetupIncompleteCard';
import {
    getMandatoryProfileSetupMissingItems,
    type ProfileSetupMissingItem,
} from '../profileSetupBanner';

const INLINE_SECTION_LABEL: Record<
    Exclude<ProfileInlineEditSection, null>,
    string
> = {
    company: 'Company Details',
    personal: 'Personal Details',
    login: 'Login & Security',
    contractor_company: 'Company details',
    contractor_services: 'Services',
    contractor_terms: 'Terms and conditions',
    contractor_availability: 'Availability',
};

const CONTRACTOR_INLINE_SECTIONS: Exclude<ProfileInlineEditSection, null>[] = [
    'contractor_company',
    'contractor_services',
    'contractor_availability',
    'contractor_terms',
];

const PROFILE_PHOTO_UNSAVED_LABEL = 'Profile photo';

type UnsavedProfilePrompt =
    | {
          kind: 'switch-inline';
          to: Exclude<ProfileInlineEditSection, null>;
      }
    | { kind: 'open-photo'; file?: File }
    | {
          kind: 'leave-photo';
          to: Exclude<ProfileInlineEditSection, null>;
      };

function cloneFormValues<T>(value: T): T {
    try {
        return structuredClone(value);
    } catch {
        return JSON.parse(JSON.stringify(value)) as T;
    }
}

interface ProfileFormProps {
    isEditing?: boolean;
    photo?: Media;
    contractorCompany: boolean;
    modernProfileView?: boolean;
    integratedProfileHeader?: boolean;
    /** Rendered directly under the profile title row (e.g. avatar summary), before company/personal cards */
    profileSummarySlot?: ReactNode;
    /** When true (after banner click), show mandatory missing-field guidance */
    highlightProfileSetup?: boolean;
    onProfileSetupComplete?: () => void;
}

const ProfileForm = ({
    photo,
    isEditing = false,
    contractorCompany,
    modernProfileView = false,
    integratedProfileHeader = false,
    profileSummarySlot,
    highlightProfileSetup = false,
    onProfileSetupComplete,
}: ProfileFormProps) => {
    const isIpad = useMediaQuery('(max-width: 1200px)');
    const isMobile = useMediaQuery('(max-width: 770px)');
    const theme = useTheme();
    const loggedInUser = UserRole();
    const navigate = useNavigate();

    const [editProfile] = useEditProfileDetailsMutation();
    const [editCompany] = useEditCompanyMutation();

    const [inlineEditSection, setInlineEditSection] =
        useState<ProfileInlineEditSection>(null);
    const [unsavedPrompt, setUnsavedPrompt] =
        useState<UnsavedProfilePrompt | null>(null);
    const unsavedSwitchDialogOpen = unsavedPrompt !== null;
    const inlineEditSectionRef = useRef<ProfileInlineEditSection>(null);
    const inlineSnapshotRef = useRef<Record<string, unknown> | null>(null);
    const profilePhotoInlineControllerRef =
        useRef<ProfilePhotoInlineController>({
            isPhotoEditActive: false,
            completeOpenPhoto: () => {},
            cancelPhotoEdit: () => {},
            endPhotoEditAfterSuccessfulSubmit: () => {},
        });

    useEffect(() => {
        inlineEditSectionRef.current = inlineEditSection;
    }, [inlineEditSection]);

    const ProfileSchema = getProfileSchema({ contractorCompany });
    type Profile = z.infer<typeof ProfileSchema>;

    const methods = useForm<Profile>({
        defaultValues: {
            isActive: false,
            company: {
                allowsMultipleDomains: false,
                isActive: false,
            },
            services: {
                type: 'catering',
            },
            availableDays: [],
            unavailablePeriods: [],
        },
        resolver: zodResolver(ProfileSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });

    const {
        handleSubmit,
        setValue,
        getValues,
        reset,
        control,
        formState: { isDirty },
    } = methods;

    const { dirtyFields } = useFormState({ control });

    const modernShell = Boolean(modernProfileView && !isEditing);

    const mobileInlineEditScrollPadding =
        modernShell && isMobile && inlineEditSection !== null
            ? profileMobileInlineEditBottomReserve(theme)
            : undefined;

    const openInlineSection = useCallback(
        (section: Exclude<ProfileInlineEditSection, null>) => {
            inlineSnapshotRef.current = cloneFormValues(
                getValues() as Record<string, unknown>,
            );
            setInlineEditSection(section);
        },
        [getValues],
    );

    const cancelInlineEdit = useCallback(() => {
        if (inlineSnapshotRef.current) {
            reset(inlineSnapshotRef.current as Profile);
        }
        setInlineEditSection(null);
        inlineSnapshotRef.current = null;
    }, [reset]);

    const beginInlineEdit = useCallback(
        (section: Exclude<ProfileInlineEditSection, null>) => {
            if (inlineEditSection === section) return;

            const photoApi = profilePhotoInlineControllerRef.current;
            if (photoApi.isPhotoEditActive) {
                if (
                    profilePictureIsDirty(
                        dirtyFields as Record<string, unknown> | undefined,
                    )
                ) {
                    setUnsavedPrompt({ kind: 'leave-photo', to: section });
                    return;
                }
                photoApi.cancelPhotoEdit();
            }

            if (
                inlineEditSection &&
                inlineEditSection !== section &&
                profileInlineSectionHasDirtyChanges(
                    inlineEditSection,
                    dirtyFields as Record<string, unknown>,
                    contractorCompany,
                )
            ) {
                setUnsavedPrompt({ kind: 'switch-inline', to: section });
                return;
            }

            if (inlineEditSection && inlineEditSection !== section) {
                cancelInlineEdit();
            }

            openInlineSection(section);
        },
        [
            inlineEditSection,
            dirtyFields,
            contractorCompany,
            cancelInlineEdit,
            openInlineSection,
        ],
    );

    const finishOpenPhotoFlow = useCallback((file?: File) => {
        const photoApi = profilePhotoInlineControllerRef.current;
        if (file) {
            photoApi.completeOpenPhoto(file);
            return;
        }
        if (photoApi.openNativePhotoPicker) {
            photoApi.completeOpenPhoto();
            photoApi.openNativePhotoPicker();
            return;
        }
        photoApi.completeOpenPhoto();
    }, []);

    const tryBeginPhotoEdit = useCallback(() => {
        if (
            inlineEditSection &&
            profileInlineSectionHasDirtyChanges(
                inlineEditSection,
                dirtyFields as Record<string, unknown>,
                contractorCompany,
            )
        ) {
            setUnsavedPrompt({ kind: 'open-photo' });
            return;
        }
        if (
            inlineEditSection &&
            !profileInlineSectionHasDirtyChanges(
                inlineEditSection,
                dirtyFields as Record<string, unknown>,
                contractorCompany,
            )
        ) {
            cancelInlineEdit();
        }
        finishOpenPhotoFlow();
    }, [
        inlineEditSection,
        dirtyFields,
        contractorCompany,
        cancelInlineEdit,
        finishOpenPhotoFlow,
    ]);

    const requestUnsavedOpenPhoto = useCallback(
        (file?: File) => {
            if (
                inlineEditSection &&
                profileInlineSectionHasDirtyChanges(
                    inlineEditSection,
                    dirtyFields as Record<string, unknown>,
                    contractorCompany,
                )
            ) {
                setUnsavedPrompt({ kind: 'open-photo', file });
                return;
            }
            if (
                inlineEditSection &&
                !profileInlineSectionHasDirtyChanges(
                    inlineEditSection,
                    dirtyFields as Record<string, unknown>,
                    contractorCompany,
                )
            ) {
                cancelInlineEdit();
            }
            finishOpenPhotoFlow(file);
        },
        [
            inlineEditSection,
            dirtyFields,
            contractorCompany,
            cancelInlineEdit,
            finishOpenPhotoFlow,
        ],
    );

    const finishInlineIfNeeded = useCallback(() => {
        if (modernProfileView && inlineEditSectionRef.current !== null) {
            setInlineEditSection(null);
            inlineSnapshotRef.current = null;
        }
    }, [modernProfileView]);

    /** One RHF form backs company + personal + login; pause API-driven resets whenever any inline section is open so sibling effects do not clear dirty state. */
    const suspendCompanyResets = modernShell && inlineEditSection !== null;

    const suspendProfileResets = modernShell && inlineEditSection !== null;

    const suspendContractorResets =
        modernShell && contractorCompany && inlineEditSection !== null;

    const companyFieldsEditing =
        isEditing ||
        inlineEditSection === 'company' ||
        (contractorCompany &&
            inlineEditSection != null &&
            CONTRACTOR_INLINE_SECTIONS.includes(
                inlineEditSection as (typeof CONTRACTOR_INLINE_SECTIONS)[number],
            ));

    const personalFieldsEditing = isEditing || inlineEditSection === 'personal';

    const loginFieldsEditing = isEditing || inlineEditSection === 'login';

    useEffect(() => {
        if (!loggedInUser?.role) return;

        setValue('role' as any, loggedInUser.role as any, {
            shouldDirty: false,
            shouldValidate: true,
        });
    }, [loggedInUser?.role, setValue]);

    useEffect(() => {
        if (!photo) return;
        setValue('profilePicture' as any, photo as any, {
            shouldDirty: true,
            shouldValidate: true,
        });
    }, [photo, setValue]);

    const handleBack = useCallback(() => {
        navigate(contractorCompany ? '/contractorProfile' : '/profile');
    }, [navigate, contractorCompany]);

    const onInvalid = useCallback((formErrors: any) => {
        console.error(formErrors);
        toast.error('Please fix the highlighted fields');
    }, []);

    const onSubmit = useCallback(
        async (data: Profile) => {
            try {
                const userRole = loggedInUser?.role;

                if (!userRole) return;

                if (contractorCompany) {
                    const url = `contractors/${loggedInUser.company}`;

                    await editCompany([url, data]).unwrap();
                    toast.success('Profile updated successfully');
                    finishInlineIfNeeded();
                    if (isEditing) {
                        handleBack();
                    }
                    return;
                }

                const isGuestCompanyAdmin =
                    userRole === 'guest_admin' ||
                    userRole === 'guest_department_admin';

                if (isGuestCompanyAdmin) {
                    const url = `/guests/${loggedInUser?.company}`;
                    await editCompany([url, (data as any).company]).unwrap();

                    const { role, company, ...rest } = data as any;
                    await editProfile(rest).unwrap();

                    toast.success('Profile updated successfully');
                    finishInlineIfNeeded();
                    if (isEditing) {
                        handleBack();
                    }
                    return;
                }

                if (
                    userRole === 'guest_company' ||
                    userRole === 'owner_company'
                ) {
                    const url =
                        userRole === 'guest_company'
                            ? `/guests/${loggedInUser?.company}`
                            : `/owners/${loggedInUser?.company}`;

                    await editCompany([url, (data as any).company]).unwrap();
                    toast.success('Profile updated successfully');
                    finishInlineIfNeeded();
                    if (isEditing) {
                        handleBack();
                    }
                    return;
                }

                const { role, ...rest } = data as any;
                await editProfile(rest).unwrap();
                toast.success('Profile updated successfully');
                finishInlineIfNeeded();
                if (isEditing) {
                    handleBack();
                }
            } catch (error) {
                toast.error('Error updating profile');
                throw error;
            }
        },
        [
            loggedInUser,
            contractorCompany,
            editCompany,
            editProfile,
            finishInlineIfNeeded,
            isEditing,
            handleBack,
            dirtyFields,
        ],
    );

    const saveInlineEdit = useCallback(() => {
        return handleSubmit(onSubmit, onInvalid)();
    }, [handleSubmit, onSubmit, onInvalid]);

    const handleUnsavedDialogStay = useCallback(() => {
        setUnsavedPrompt(null);
    }, []);

    const handleUnsavedDiscard = useCallback(() => {
        const prompt = unsavedPrompt;
        setUnsavedPrompt(null);
        if (!prompt) return;
        if (prompt.kind === 'switch-inline') {
            cancelInlineEdit();
            openInlineSection(prompt.to);
            return;
        }
        if (prompt.kind === 'open-photo') {
            cancelInlineEdit();
            finishOpenPhotoFlow(prompt.file);
            return;
        }
        if (prompt.kind === 'leave-photo') {
            profilePhotoInlineControllerRef.current.cancelPhotoEdit();
            openInlineSection(prompt.to);
        }
    }, [
        unsavedPrompt,
        cancelInlineEdit,
        openInlineSection,
        finishOpenPhotoFlow,
    ]);

    const handleUnsavedSave = useCallback(() => {
        const prompt = unsavedPrompt;
        void handleSubmit(async (data: Profile) => {
            await onSubmit(data);
            setUnsavedPrompt(null);
            if (prompt?.kind === 'switch-inline') {
                openInlineSection(prompt.to);
            } else if (prompt?.kind === 'open-photo') {
                cancelInlineEdit();
                finishOpenPhotoFlow(prompt.file);
            } else if (prompt?.kind === 'leave-photo') {
                profilePhotoInlineControllerRef.current.endPhotoEditAfterSuccessfulSubmit();
                openInlineSection(prompt.to);
            }
        }, onInvalid)();
    }, [
        unsavedPrompt,
        handleSubmit,
        onSubmit,
        onInvalid,
        openInlineSection,
        cancelInlineEdit,
        finishOpenPhotoFlow,
    ]);

    const inlineUxValue = useMemo(
        () => ({
            inlineEditSection,
            beginInlineEdit,
            cancelInlineEdit,
            saveInlineEdit,
            inlineEditSnapshotRef: inlineSnapshotRef,
            suspendCompanyResets,
            suspendProfileResets,
            suspendContractorResets,
            profilePhotoInlineControllerRef,
            tryBeginPhotoEdit,
            requestUnsavedOpenPhoto,
        }),
        [
            inlineEditSection,
            beginInlineEdit,
            cancelInlineEdit,
            saveInlineEdit,
            suspendCompanyResets,
            suspendProfileResets,
            suspendContractorResets,
            tryBeginPhotoEdit,
            requestUnsavedOpenPhoto,
        ],
    );

    const unsavedDialogAreaLabel = (() => {
        if (!unsavedPrompt) return 'this section';
        if (unsavedPrompt.kind === 'leave-photo') {
            return PROFILE_PHOTO_UNSAVED_LABEL;
        }
        if (
            unsavedPrompt.kind === 'open-photo' ||
            unsavedPrompt.kind === 'switch-inline'
        ) {
            return inlineEditSection
                ? INLINE_SECTION_LABEL[inlineEditSection]
                : 'this section';
        }
        return 'this section';
    })();

    const { data: profileDetailsForSetup } = useGetProfileDetailsQuery(
        '/profile',
        { skip: !highlightProfileSetup },
    );
    const formProfilePicture = useWatch({
        control,
        name: 'profilePicture',
    }) as Media | undefined;

    const profileSetupMissingItems = useMemo(() => {
        if (!highlightProfileSetup) return [];
        const apiPicture = profileDetailsForSetup?.profilePicture as
            | (Media & { valid?: boolean })
            | undefined;
        const formHasReplacement =
            Boolean(formProfilePicture?.id) &&
            formProfilePicture?.id !== apiPicture?.id;
        if (formHasReplacement) return [];
        return getMandatoryProfileSetupMissingItems(
            {
                profilePicture: apiPicture ?? formProfilePicture,
            },
            loggedInUser?.role,
        );
    }, [
        highlightProfileSetup,
        profileDetailsForSetup?.profilePicture,
        formProfilePicture,
        loggedInUser?.role,
    ]);

    useEffect(() => {
        if (
            highlightProfileSetup &&
            profileSetupMissingItems.length === 0 &&
            onProfileSetupComplete
        ) {
            onProfileSetupComplete();
        }
    }, [
        highlightProfileSetup,
        profileSetupMissingItems.length,
        onProfileSetupComplete,
    ]);

    const handleProfileSetupAddItem = useCallback(
        (item: ProfileSetupMissingItem) => {
            if (
                item.id === 'profilePicture' ||
                item.id === 'profilePictureInvalid'
            ) {
                document
                    .getElementById('profile-setup-photo')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                tryBeginPhotoEdit();
            }
        },
        [tryBeginPhotoEdit],
    );

    return (
        <FormProvider {...methods}>
            <ProfileInlineUxContext.Provider value={inlineUxValue}>
                <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
                    {integratedProfileHeader && modernShell && (
                        <Stack spacing={3} sx={{ width: '100%', mb: 3 }}>
                            <Box>
                                <Box
                                    sx={{
                                        display: 'inline-block',
                                        position: 'relative',
                                        pb: 1.25,
                                        mb: 0.5,
                                    }}
                                >
                                    <Typography
                                        variant='h1'
                                        sx={{ textTransform: 'uppercase' }}
                                    >
                                        Profile
                                    </Typography>
                                </Box>
                            </Box>
                            {highlightProfileSetup &&
                                profileSetupMissingItems.length > 0 && (
                                    <ProfileSetupIncompleteCard
                                        missingItems={profileSetupMissingItems}
                                        onAddItem={handleProfileSetupAddItem}
                                    />
                                )}
                            {profileSummarySlot}
                        </Stack>
                    )}

                    <Stack
                        spacing={modernShell ? 3 : 0}
                        sx={{
                            width: '100%',
                            ...(mobileInlineEditScrollPadding
                                ? { pb: mobileInlineEditScrollPadding }
                                : {}),
                        }}
                    >
                        {loggedInUser?.role === 'admin' ||
                        loggedInUser?.role ===
                            'external_attendee_user' ? null : loggedInUser?.role ===
                              'owner_company' ||
                          loggedInUser?.role === 'owner_admin' ||
                          loggedInUser?.role === 'owner_user' ? (
                            <OwnerCompanyDetails
                                isEditing={companyFieldsEditing}
                                modernProfileView={modernProfileView}
                                contractorCompany={contractorCompany}
                            />
                        ) : contractorCompany ? (
                            <ContractorCompanyDetails
                                isEditing={companyFieldsEditing}
                                modernProfileView={modernProfileView}
                            />
                        ) : loggedInUser?.role.includes('guest') ? (
                            <GuestCompanyDetails
                                isEditing={companyFieldsEditing}
                                modernProfileView={modernProfileView}
                                contractorCompany={contractorCompany}
                            />
                        ) : null}

                        {loggedInUser?.role === 'owner_company' ||
                        loggedInUser?.role === 'guest_company' ||
                        contractorCompany ? null : (
                            <PersonalDetailsComponent
                                isEditing={personalFieldsEditing}
                                isEditingLoginFields={loginFieldsEditing}
                                modernProfileView={modernProfileView}
                                contractorCompany={contractorCompany}
                            />
                        )}
                    </Stack>

                    {isEditing && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent:
                                    isMobile || isIpad
                                        ? 'flex-end'
                                        : 'space-between',
                                py: 2,
                            }}
                        >
                            <Button
                                type='button'
                                startIcon={<ArrowBack />}
                                onClick={handleBack}
                                sx={{
                                    display:
                                        isMobile || isIpad ? 'none' : 'flex',
                                }}
                            >
                                Back to Profile
                            </Button>

                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    alignItems: 'center',
                                }}
                            >
                                {isDirty && (
                                    <Typography
                                        variant='caption'
                                        sx={{ color: 'text.primary' }}
                                    >
                                        You have unsaved changes
                                    </Typography>
                                )}
                                <Button variant='contained' type='submit'>
                                    Save
                                </Button>
                            </Box>
                        </Box>
                    )}
                </form>
                {modernShell && (
                    <Dialog
                        open={unsavedSwitchDialogOpen}
                        onClose={handleUnsavedDialogStay}
                        aria-labelledby='unsaved-inline-profile-title'
                        maxWidth={isMobile ? false : 'sm'}
                        fullWidth
                        scroll='paper'
                        PaperProps={{
                            elevation: 0,
                            sx: {
                                position: 'relative',
                                overflow: 'hidden',
                                ...(isMobile
                                    ? {
                                          m: 0,
                                          maxWidth: '100%',
                                          width: '100%',
                                          maxHeight: 'min(92vh, 560px)',
                                          borderRadius: '20px 20px 0 0',
                                          bgcolor: 'background.paper',
                                          backgroundImage: 'none',
                                          pb: `calc(${theme.spacing(1)} + env(safe-area-inset-bottom, 0px))`,
                                      }
                                    : {
                                          maxWidth: 460,
                                          width: '100%',
                                          borderRadius: 3,
                                          overflow: 'hidden',
                                          bgcolor: 'background.paper',
                                          backgroundImage: 'none',
                                          boxShadow:
                                              '0 4px 6px rgba(20, 55, 74, 0.06), 0 12px 32px rgba(20, 55, 74, 0.12)',
                                      }),
                            },
                        }}
                        sx={{
                            '& .MuiDialog-container': {
                                alignItems: {
                                    xs: 'flex-end',
                                    sm: 'center',
                                },
                            },
                        }}
                    >
                        {isMobile && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    pt: 1.25,
                                    pb: 0.5,
                                }}
                                aria-hidden
                            >
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 4,
                                        borderRadius: 2,
                                        bgcolor: 'action.disabledBackground',
                                    }}
                                />
                            </Box>
                        )}
                        <IconButton
                            type='button'
                            aria-label='Close dialog'
                            onClick={handleUnsavedDialogStay}
                            size='small'
                            sx={{
                                display: { xs: 'none', sm: 'inline-flex' },
                                position: 'absolute',
                                right: 10,
                                top: 10,
                                zIndex: 1,
                                color: 'action.active',
                                '&:hover': {
                                    bgcolor: 'action.hover',
                                    color: 'text.secondary',
                                },
                            }}
                        >
                            <CloseIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                        <DialogContent>
                            <Stack
                                direction='row'
                                spacing={{ xs: 2, sm: 2.5 }}
                                alignItems='flex-start'
                            >
                                <Box
                                    sx={{
                                        width: { xs: 48, sm: 52 },
                                        height: { xs: 48, sm: 52 },
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        bgcolor: (t) =>
                                            alpha(t.palette.yellow.main, 0.14),
                                    }}
                                >
                                    <WarningAmberIcon
                                        sx={{
                                            fontSize: { xs: 28, sm: 30 },
                                            color: 'highlight.main',
                                        }}
                                    />
                                </Box>
                                <Box
                                    sx={{
                                        minWidth: 0,
                                        pt: { xs: 0.25, sm: 0.5 },
                                    }}
                                >
                                    <Typography
                                        id='unsaved-inline-profile-title'
                                        component='h2'
                                        variant='body1'
                                        sx={{
                                            fontWeight: 700,
                                            color: 'primary.dark',
                                            lineHeight: 1.4,
                                        }}
                                    >
                                        Unsaved changes
                                    </Typography>
                                    {isMobile ? (
                                        <Typography
                                            variant='body2'
                                            sx={{
                                                mt: 1.5,
                                                color: 'text.secondary',
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            You have unsaved changes in{' '}
                                            <Box
                                                component='span'
                                                sx={{
                                                    fontWeight: 700,
                                                    color: 'text.primary',
                                                }}
                                            >
                                                {unsavedDialogAreaLabel}
                                            </Box>
                                            . Save before closing?
                                        </Typography>
                                    ) : (
                                        <Typography
                                            variant='body2'
                                            sx={{
                                                mt: 2,
                                                color: 'text.secondary',
                                                lineHeight: 1.55,
                                            }}
                                        >
                                            You have unsaved changes in{' '}
                                            <Box
                                                component='span'
                                                sx={{
                                                    fontWeight: 700,
                                                    color: 'primary.dark',
                                                }}
                                            >
                                                {unsavedDialogAreaLabel}
                                            </Box>
                                            . Would you like to save before
                                            leaving edit mode?
                                        </Typography>
                                    )}
                                </Box>
                            </Stack>
                        </DialogContent>
                        <DialogActions
                            sx={{
                                gap: { xs: 0, sm: 1.25 },
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: { xs: 'stretch', sm: 'center' },
                                justifyContent: {
                                    xs: 'flex-start',
                                    sm: 'center',
                                },
                                flexWrap: { xs: 'nowrap', sm: 'nowrap' },
                                width: '100%',
                                maxWidth: { xs: '100%', sm: 'none' },
                                alignSelf: 'stretch',
                                boxSizing: 'border-box',
                            }}
                        >
                            {isMobile ? (
                                <Stack
                                    direction='column'
                                    spacing={1.5}
                                    useFlexGap
                                    sx={{
                                        width: '100%',
                                        maxWidth: '100%',
                                        alignSelf: 'stretch',
                                        alignItems: 'stretch',
                                        boxSizing: 'border-box',
                                        px: 3,
                                        pt: 0.5,
                                        pb: 2,
                                        bgcolor: 'background.paper',
                                    }}
                                >
                                    <Button
                                        type='button'
                                        variant='contained'
                                        fullWidth
                                        disableElevation
                                        onClick={handleUnsavedSave}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: (t) =>
                                                t.typography.body2.fontSize,
                                            py: 1.25,
                                            borderRadius: 1.5,
                                            display: 'block',
                                            width: '100%',
                                            maxWidth: '100%',
                                            minWidth: 0,
                                            boxSizing: 'border-box',
                                            bgcolor: 'primary.dark',
                                            color: (t) =>
                                                t.palette.primary.contrastText,
                                            '&:hover': {
                                                bgcolor: 'primary.dark',
                                                opacity: 0.92,
                                            },
                                        }}
                                    >
                                        Save Changes
                                    </Button>
                                    <Button
                                        type='button'
                                        variant='contained'
                                        fullWidth
                                        disableElevation
                                        onClick={handleUnsavedDiscard}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: (t) =>
                                                t.typography.body2.fontSize,
                                            py: 1.25,
                                            borderRadius: 1.5,
                                            display: 'block',
                                            width: '100%',
                                            maxWidth: '100%',
                                            minWidth: 0,
                                            boxSizing: 'border-box',
                                            bgcolor: 'background.paper',
                                            color: 'error.dark',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            boxShadow: 'none',
                                            '&:hover': {
                                                bgcolor: 'action.hover',
                                                boxShadow: 'none',
                                            },
                                        }}
                                    >
                                        Discard Changes
                                    </Button>
                                    <Button
                                        type='button'
                                        variant='contained'
                                        fullWidth
                                        disableElevation
                                        onClick={handleUnsavedDialogStay}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: (t) =>
                                                t.typography.body2.fontSize,
                                            py: 1.25,
                                            borderRadius: 1.5,
                                            display: 'block',
                                            width: '100%',
                                            maxWidth: '100%',
                                            minWidth: 0,
                                            boxSizing: 'border-box',
                                            bgcolor: 'background.paper',
                                            color: 'primary.dark',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            boxShadow: 'none',
                                            '&:hover': {
                                                bgcolor: 'action.hover',
                                                boxShadow: 'none',
                                            },
                                        }}
                                    >
                                        Keep Editing
                                    </Button>
                                </Stack>
                            ) : (
                                <>
                                    <Button
                                        type='button'
                                        variant='outlined'
                                        onClick={handleUnsavedDialogStay}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: (t) =>
                                                t.typography.body2.fontSize,
                                            borderColor: 'divider',
                                            color: 'primary.dark',
                                            borderRadius: '6px',
                                            py: 1.125,
                                            px: 2,
                                            flexShrink: 0,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        Keep Editing
                                    </Button>
                                    <Button
                                        type='button'
                                        variant='outlined'
                                        onClick={handleUnsavedDiscard}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: (t) =>
                                                t.typography.body2.fontSize,
                                            borderColor: 'divider',
                                            color: (t) =>
                                                alpha(
                                                    t.palette.error.main,
                                                    0.85,
                                                ),
                                            borderRadius: '6px',
                                            py: 1.125,
                                            px: 2,
                                            flexShrink: 0,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        Discard Changes
                                    </Button>
                                    <Button
                                        type='button'
                                        variant='contained'
                                        onClick={handleUnsavedSave}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: (t) =>
                                                t.typography.body2.fontSize,
                                            borderRadius: '6px',
                                            py: 1.125,
                                            px: 2,
                                            flexShrink: 0,
                                            whiteSpace: 'nowrap',
                                            bgcolor: 'primary.dark',
                                            color: (t) =>
                                                t.palette.primary.contrastText,
                                            boxShadow: 'none',
                                            '&:hover': {
                                                bgcolor: 'primary.dark',
                                                boxShadow: 'none',
                                                opacity: 0.92,
                                            },
                                        }}
                                    >
                                        Save Changes
                                    </Button>
                                </>
                            )}
                        </DialogActions>
                    </Dialog>
                )}
            </ProfileInlineUxContext.Provider>
        </FormProvider>
    );
};

export default ProfileForm;
